import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import {
  Bell, MessageSquare, ShoppingCart, Package,
  CreditCard, Users, AlertCircle, CheckCircle, Settings as SettingsIcon,
  List, Loader2, XCircle, Repeat, CornerDownLeft, UserCheck,
  DollarSign, ShoppingBag, Edit, Calendar, AlertTriangle, Send
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';

// ── Channel definitions ──
const CHANNELS = [
  { key: 'in_app',    label: 'In-App',    icon: Bell,           color: 'text-slate-600',  description: 'Bell icon notifications' },
  { key: 'push',      label: 'Push',      icon: Send,           color: 'text-blue-600',   description: 'Browser push notifications' },
  { key: 'chatwoot',  label: 'Chatwoot',  icon: MessageSquare,  color: 'text-green-600',  description: 'Girichat inbox messages' },
  { key: 'whatsapp',  label: 'WhatsApp',  icon: MessageSquare,  color: 'text-emerald-600',description: 'WABA template messages' },
];

// ── Event groups with event definitions ──
const EVENT_GROUPS = [
  {
    id: 'orders',
    label: 'Orders',
    icon: ShoppingCart,
    color: 'bg-blue-100 text-blue-600',
    description: 'Order lifecycle events',
    events: [
      { key: 'new_order',              label: 'New Order Received',       icon: ShoppingBag,    description: 'When a customer places a new order' },
      { key: 'order_cancelled',        label: 'Order Cancelled',          icon: XCircle,        description: 'When an order is cancelled' },
      { key: 'order_refund_processed', label: 'Refund Processed',         icon: CreditCard,     description: 'When a refund is completed' },
      { key: 'order_replacement',      label: 'Replacement Created',      icon: Repeat,         description: 'When a replacement order is created' },
    ],
  },
  {
    id: 'requests',
    label: 'Customer Requests',
    icon: Users,
    color: 'bg-amber-100 text-amber-600',
    description: 'Cancellation, return, exchange requests',
    events: [
      { key: 'cancellation_request',   label: 'Cancellation Request',     icon: XCircle,        description: 'Customer requests order cancellation' },
      { key: 'return_request',         label: 'Return Request',           icon: CornerDownLeft,  description: 'Customer requests a return' },
      { key: 'return_approved',        label: 'Return Approved',          icon: CheckCircle,    description: 'Return has been approved' },
      { key: 'exchange_request',       label: 'Exchange Request',         icon: Repeat,         description: 'Customer requests an exchange' },
      { key: 'customer_decision',      label: 'Customer Decision',        icon: UserCheck,      description: 'Customer responds to a proposed resolution' },
      { key: 'escalation',             label: 'Escalation',               icon: AlertTriangle,  description: 'Issue escalated — immediate attention needed' },
    ],
  },
  {
    id: 'listings',
    label: 'Listings & Leads',
    icon: Package,
    color: 'bg-purple-100 text-purple-600',
    description: 'Listing approvals and lead notifications',
    events: [
      { key: 'new_lead',              label: 'New Lead / Enquiry',        icon: Users,          description: 'Customer enquires about a listing' },
      { key: 'listing_approved',      label: 'Listing Approved',          icon: CheckCircle,    description: 'Your listing has been approved' },
      { key: 'listing_rejected',      label: 'Listing Rejected',          icon: XCircle,        description: 'Your listing was rejected — action needed' },
    ],
  },
  {
    id: 'financial',
    label: 'Financial',
    icon: CreditCard,
    color: 'bg-emerald-100 text-emerald-600',
    description: 'Payouts and payment updates',
    events: [
      { key: 'payment_received',      label: 'Payment Received',          icon: DollarSign,     description: 'A payment was received for an order' },
      { key: 'payout_processed',      label: 'Payout Processed',          icon: CreditCard,     description: 'Your payout has been initiated' },
    ],
  },
];

const Notifications = () => {
  const { toast } = useToast();
  const { apiCall } = useAuth();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('list');

  // Notification list
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);

  // Preferences
  const [prefs, setPrefs] = useState(null);

  // ── Load notifications ──
  const loadNotifications = useCallback(async (pg = 1) => {
    try {
      const data = await apiCall(`/api/vendor-notifications?page=${pg}&limit=20`);
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
      setTotalCount(data.total || 0);
    } catch { /* ignore */ }
  }, [apiCall]);

  // ── Load preferences ──
  const loadPreferences = useCallback(async () => {
    try {
      const data = await apiCall('/api/vendor-notifications/preferences');
      setPrefs(data);
    } catch { /* ignore */ }
  }, [apiCall]);

  useEffect(() => {
    Promise.all([loadNotifications(), loadPreferences()]).finally(() => setLoading(false));
  }, [loadNotifications, loadPreferences]);

  // ── Save preferences ──
  const handleSave = async () => {
    if (!prefs) return;
    setSaving(true);
    try {
      await apiCall('/api/vendor-notifications/preferences', {
        method: 'PUT',
        body: JSON.stringify({
          global_channels: prefs.global_channels,
          event_overrides: prefs.event_overrides,
          quiet_hours: prefs.quiet_hours,
        }),
      });
      toast({ title: 'Preferences Saved', description: 'Your notification settings have been updated.' });
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  // ── Toggle helpers ──
  const toggleGlobal = (channel, value) => {
    setPrefs(p => ({
      ...p,
      global_channels: { ...(p.global_channels || {}), [channel]: value },
    }));
  };

  const toggleEvent = (eventKey, channel, value) => {
    setPrefs(p => ({
      ...p,
      event_overrides: {
        ...(p.event_overrides || {}),
        [eventKey]: {
          ...(p.event_overrides?.[eventKey] || {}),
          [channel]: value,
        },
      },
    }));
  };

  // ── Mark notification read ──
  const markRead = async (id) => {
    try {
      await apiCall(`/api/vendor-notifications/${id}/read`, { method: 'POST' });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch { /* ignore */ }
  };

  const markAllRead = async () => {
    try {
      await apiCall('/api/vendor-notifications/read-all', { method: 'POST' });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch { /* ignore */ }
  };

  // ── Event icon lookup ──
  const getEventIcon = (type) => {
    for (const group of EVENT_GROUPS) {
      const ev = group.events.find(e => e.key === type);
      if (ev) return ev.icon;
    }
    return Bell;
  };

  const getEventColor = (type) => {
    const colorMap = {
      new_order: 'bg-blue-500', order_cancelled: 'bg-red-500', order_refund_processed: 'bg-amber-500',
      cancellation_request: 'bg-red-400', return_request: 'bg-orange-500', exchange_request: 'bg-purple-500',
      escalation: 'bg-red-600', new_lead: 'bg-green-500', listing_approved: 'bg-emerald-500',
      listing_rejected: 'bg-red-400', payment_received: 'bg-green-600', payout_processed: 'bg-teal-500',
    };
    return colorMap[type] || 'bg-gray-500';
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64 text-gray-500">
          <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading...
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-4 md:space-y-6 max-w-4xl" data-testid="notifications-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
            <p className="text-sm text-gray-500 mt-1">Stay updated with your business activities</p>
          </div>
          {unreadCount > 0 && (
            <Badge className="bg-blue-600 text-white">{unreadCount} unread</Badge>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="list" className="flex items-center gap-2">
              <List className="h-4 w-4" />
              <span>All Notifications</span>
              {unreadCount > 0 && <Badge variant="secondary" className="text-[10px] ml-1">{unreadCount}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <SettingsIcon className="h-4 w-4" />
              <span>Channel Settings</span>
            </TabsTrigger>
          </TabsList>

          {/* ═══ Notifications List ═══ */}
          <TabsContent value="list" className="space-y-4 mt-4">
            {unreadCount > 0 && (
              <div className="flex justify-end">
                <Button variant="ghost" size="sm" onClick={markAllRead} className="text-xs text-gray-500" data-testid="mark-all-read-btn">
                  Mark all as read
                </Button>
              </div>
            )}

            {notifications.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-16 text-center">
                  <Bell className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium text-gray-600">No notifications yet</p>
                  <p className="text-sm text-gray-400 mt-1">You'll see order updates, leads, and more here.</p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-0 divide-y">
                  {notifications.map((n) => {
                    const Icon = getEventIcon(n.type);
                    return (
                      <div
                        key={n.id}
                        className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${!n.read ? 'bg-blue-50/40' : ''}`}
                        onClick={() => !n.read && markRead(n.id)}
                        data-testid={`notification-${n.id}`}
                      >
                        <div className="flex gap-3">
                          <div className={`${getEventColor(n.type)} rounded-full p-2 h-9 w-9 flex items-center justify-center shrink-0`}>
                            <Icon className="h-4 w-4 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <h4 className={`text-sm ${!n.read ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'}`}>{n.title}</h4>
                              <div className="flex items-center gap-1.5 shrink-0">
                                {!n.read && <span className="w-2 h-2 rounded-full bg-blue-500" />}
                                {n.priority === 'urgent' && <Badge variant="destructive" className="text-[9px] px-1.5 py-0">Urgent</Badge>}
                              </div>
                            </div>
                            <p className="text-sm text-gray-500 mt-0.5 line-clamp-2">{n.message}</p>
                            <div className="flex items-center gap-3 mt-1.5">
                              <span className="text-[11px] text-gray-400">
                                {n.created_at ? new Date(n.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                              </span>
                              {n.sent_channels?.length > 0 && (
                                <div className="flex gap-1">
                                  {n.sent_channels.map(ch => (
                                    <span key={ch} className="text-[9px] bg-gray-100 text-gray-500 rounded px-1.5 py-0.5">{ch}</span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            )}

            {totalCount > 20 && (
              <div className="flex justify-center gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => { setPage(p => p - 1); loadNotifications(page - 1); }}>Previous</Button>
                <span className="text-sm text-gray-500 py-2">Page {page}</span>
                <Button variant="outline" size="sm" disabled={notifications.length < 20} onClick={() => { setPage(p => p + 1); loadNotifications(page + 1); }}>Next</Button>
              </div>
            )}
          </TabsContent>

          {/* ═══ Channel Settings ═══ */}
          <TabsContent value="settings" className="mt-4 space-y-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Channel Preferences</h2>
                <p className="text-sm text-gray-500">Choose how you receive notifications for each event type</p>
              </div>
              <Button onClick={handleSave} disabled={saving} size="sm" data-testid="save-prefs-btn">
                {saving ? <><Loader2 className="h-4 w-4 animate-spin mr-1.5" /> Saving...</> : 'Save Changes'}
              </Button>
            </div>

            {/* Global Channel Toggles */}
            <Card data-testid="global-channels-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Global Channel Defaults</CardTitle>
                <CardDescription className="text-xs">Master toggles — disable a channel here to turn it off for all events</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {CHANNELS.map(ch => {
                    const ChIcon = ch.icon;
                    const enabled = prefs?.global_channels?.[ch.key] ?? true;
                    return (
                      <div key={ch.key} className={`flex items-center justify-between p-3 rounded-lg border ${enabled ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-50 opacity-60'}`}>
                        <div className="flex items-center gap-2">
                          <ChIcon className={`h-4 w-4 ${ch.color}`} />
                          <span className="text-sm font-medium">{ch.label}</span>
                        </div>
                        <Switch
                          checked={enabled}
                          onCheckedChange={(v) => toggleGlobal(ch.key, v)}
                          data-testid={`global-toggle-${ch.key}`}
                        />
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Per-Event Channel Matrix */}
            {EVENT_GROUPS.map(group => {
              const GroupIcon = group.icon;
              return (
                <Card key={group.id} data-testid={`event-group-${group.id}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${group.color}`}>
                        <GroupIcon className="h-4 w-4" />
                      </div>
                      <div>
                        <CardTitle className="text-base">{group.label}</CardTitle>
                        <CardDescription className="text-xs">{group.description}</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    {/* Column headers */}
                    <div className="hidden sm:grid sm:grid-cols-[1fr_repeat(4,56px)] gap-1 mb-2 px-1">
                      <span />
                      {CHANNELS.map(ch => (
                        <span key={ch.key} className="text-[10px] text-gray-400 text-center font-medium uppercase tracking-wider">{ch.label}</span>
                      ))}
                    </div>

                    <div className="divide-y divide-gray-100">
                      {group.events.map(ev => {
                        const EvIcon = ev.icon;
                        const overrides = prefs?.event_overrides?.[ev.key] || {};
                        return (
                          <div key={ev.key} className="py-3 sm:grid sm:grid-cols-[1fr_repeat(4,56px)] sm:items-center gap-1" data-testid={`event-row-${ev.key}`}>
                            <div className="flex items-center gap-2 mb-2 sm:mb-0">
                              <EvIcon className="h-4 w-4 text-gray-400 shrink-0" />
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-800 truncate">{ev.label}</p>
                                <p className="text-[11px] text-gray-400 truncate hidden md:block">{ev.description}</p>
                              </div>
                            </div>
                            <div className="flex sm:contents gap-3 sm:gap-0">
                              {CHANNELS.map(ch => {
                                const globalOn = prefs?.global_channels?.[ch.key] ?? true;
                                const eventOn = overrides[ch.key] ?? true;
                                return (
                                  <div key={ch.key} className="flex items-center justify-center sm:justify-center">
                                    <Switch
                                      checked={globalOn && eventOn}
                                      disabled={!globalOn}
                                      onCheckedChange={(v) => toggleEvent(ev.key, ch.key, v)}
                                      className="scale-90"
                                      data-testid={`toggle-${ev.key}-${ch.key}`}
                                    />
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              );
            })}

            {/* Quiet Hours */}
            <Card data-testid="quiet-hours-card">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-slate-100 text-slate-600">
                    <AlertCircle className="h-4 w-4" />
                  </div>
                  <div>
                    <CardTitle className="text-base">Quiet Hours</CardTitle>
                    <CardDescription className="text-xs">Suppress push & WhatsApp during these hours (in-app still stored)</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <Switch
                    checked={prefs?.quiet_hours?.enabled || false}
                    onCheckedChange={(v) => setPrefs(p => ({ ...p, quiet_hours: { ...(p.quiet_hours || {}), enabled: v } }))}
                    data-testid="quiet-hours-toggle"
                  />
                  <span className="text-sm text-gray-600">
                    {prefs?.quiet_hours?.enabled
                      ? `Active: ${prefs.quiet_hours.start || '22:00'} – ${prefs.quiet_hours.end || '08:00'}`
                      : 'Disabled — notifications sent anytime'}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Bottom Save */}
            <div className="flex justify-end pb-4">
              <Button onClick={handleSave} disabled={saving} size="lg" data-testid="save-prefs-btn-bottom">
                {saving ? 'Saving...' : 'Save All Changes'}
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default Notifications;
